import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MarkdownModule } from 'ngx-markdown';
import { map } from 'rxjs';
import { AuthService } from '../../core/services/auth/auth.service';
import { Release } from '../../models/release';
import { TestCase } from '../../models/test-case';
import { TestCaseResultInsertRequest } from '../../models/test-case-result-insert-request';
import { WorkItem } from '../../models/work-item';
import { TestCaseResultService } from '../../services/test-case-result/test-case-result.service';
import { TestCaseService } from '../../services/test-case/test-case.service';
import { FailTestCaseDialogComponent } from '../fail-test-case-dialog/fail-test-case-dialog.component';
import { CommentThreadComponent } from './comment-thread/comment-thread.component';
import { ReleaseSwitcherComponent } from '../../ui/release-switcher/release-switcher.component';
import { UserMenuComponent } from '../../ui/user-menu/user-menu.component';

type NodeStatus = 'passed' | 'failed' | 'current' | 'remaining';

/**
 * Execute Test Case screen (PBI 55100). A focused, full-width execution view with a
 * numbered stepper across the plan's test cases, the test card (title / description /
 * steps), and It passed / It failed / Skip actions.
 */
@Component({
  selector: 'app-test-case',
  standalone: true,
  imports: [CommentThreadComponent, MarkdownModule, ReleaseSwitcherComponent, UserMenuComponent],
  templateUrl: './test-case.component.html',
  styleUrl: './test-case.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestCaseComponent {
  private auth = inject(AuthService);
  private testCaseService = inject(TestCaseService);
  private resultService = inject(TestCaseResultService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  readonly workItem = toSignal(
    this.route.data.pipe(map((d) => d['testCaseData'] as WorkItem | null)),
    { initialValue: null },
  );
  readonly release = toSignal(
    this.route.data.pipe(map((d) => d['releaseData'] as Release | null)),
    {
      initialValue: null,
    },
  );
  readonly currentTestCaseId = toSignal(
    this.route.paramMap.pipe(map((p) => Number(p.get('testCaseId')))),
    { initialValue: 0 },
  );
  readonly planId = toSignal(
    this.route.queryParamMap.pipe(
      map((q) => {
        const v = Number(q.get('planId'));
        return v > 0 ? v : null;
      }),
    ),
    { initialValue: null },
  );

  readonly planTestCases = signal<TestCase[]>([]);
  /** Optimistic per-test result so the stepper updates before the next load. */
  readonly localStatuses = signal<Record<number, 'passed' | 'failed'>>({});
  readonly isSaving = signal(false);

  private loadedPlanId: number | null = null;

  constructor() {
    // load the plan's test cases (drives the stepper) once the planId is known
    this.route.queryParamMap.subscribe((q) => {
      const pid = Number(q.get('planId'));
      if (pid > 0 && pid !== this.loadedPlanId) {
        this.loadedPlanId = pid;
        this.testCaseService.getByTestPlanId(pid).subscribe({
          next: (list) => this.planTestCases.set(list),
          error: () => this.planTestCases.set([]),
        });
      }
    });
  }

  /** Plan suite + name for the breadcrumb / eyebrow. */
  readonly context = computed(() => {
    const r = this.release();
    const pid = this.planId();
    if (!r || pid == null) return null;
    for (const s of r.suites ?? []) {
      const plan = (s.testPlanSummaries ?? []).find((p) => p.testPlanId === pid);
      if (plan) return { suiteName: s.suiteName, planName: plan.testPlanName };
    }
    return null;
  });

  readonly currentIndex = computed(() =>
    this.planTestCases().findIndex((tc) => tc.testCaseId === this.currentTestCaseId()),
  );
  readonly total = computed(() => this.planTestCases().length);

  readonly stepperNodes = computed(() => {
    const id = this.currentTestCaseId();
    const local = this.localStatuses();
    return this.planTestCases().map((tc, i) => {
      let status: NodeStatus;
      if (local[tc.testCaseId]) status = local[tc.testCaseId];
      else if (tc.testCaseId === id) status = 'current';
      else if (tc.isPassing === true) status = 'passed';
      else if (tc.isPassing === false) status = 'failed';
      else status = 'remaining';
      return { testCaseId: tc.testCaseId, num: i + 1, status };
    });
  });

  readonly canPrev = computed(() => this.currentIndex() > 0);
  readonly canNext = computed(() => {
    const i = this.currentIndex();
    return i >= 0 && i < this.total() - 1;
  });

  // ---- navigation ----

  private goToIndex(index: number): void {
    const list = this.planTestCases();
    const r = this.release();
    if (!r || index < 0 || index >= list.length) return;
    this.router.navigate(['/releases', r.releaseId, 'test-case', list[index].testCaseId], {
      queryParams: { planId: this.planId() },
    });
  }

  prev(): void {
    if (this.canPrev()) this.goToIndex(this.currentIndex() - 1);
  }

  next(): void {
    if (this.canNext()) this.goToIndex(this.currentIndex() + 1);
  }

  goToTestCase(testCaseId: number): void {
    this.goToIndex(this.planTestCases().findIndex((tc) => tc.testCaseId === testCaseId));
  }

  back(): void {
    const r = this.release();
    if (r) this.router.navigate(['/releases', r.releaseId]);
  }

  /** After a result, move to the next test; from the last test, return to the release home. */
  private advance(): void {
    if (this.canNext()) this.goToIndex(this.currentIndex() + 1);
    else this.back();
  }

  // ---- actions ----

  pass(): void {
    const userId = this.auth.userSig()?.userId;
    const id = this.currentTestCaseId();
    if (userId == null || !id) return;
    this.isSaving.set(true);
    const request: TestCaseResultInsertRequest = {
      testCaseId: id,
      userId,
      isPass: true,
      comment: null,
      severity: null,
    };
    this.resultService.saveTestCaseResult(request).subscribe({
      next: () => {
        this.localStatuses.update((s) => ({ ...s, [id]: 'passed' }));
        this.advance();
      },
      complete: () => this.isSaving.set(false),
      error: () => this.isSaving.set(false),
    });
  }

  fail(): void {
    const userId = this.auth.userSig()?.userId;
    const id = this.currentTestCaseId();
    if (userId == null || !id) return;
    const ctx = this.context();
    const ref = this.dialog.open(FailTestCaseDialogComponent, {
      autoFocus: 'first-tabbable',
      panelClass: 'exec-fail-drawer',
      position: { right: '0', top: '0' },
      height: '100vh',
      width: '460px',
      data: {
        testCaseId: id,
        userId,
        testCaseName: this.workItem()?.testCaseName,
        planContext: ctx ? `${ctx.suiteName} · ${ctx.planName}` : null,
      },
    });
    ref.afterClosed().subscribe((result: { saved: boolean } | undefined) => {
      if (result?.saved) {
        this.localStatuses.update((s) => ({ ...s, [id]: 'failed' }));
        this.advance();
      }
    });
  }

  skip(): void {
    const userId = this.auth.userSig()?.userId;
    const id = this.currentTestCaseId();
    if (userId == null || !id) return;
    this.isSaving.set(true);
    this.testCaseService.skip(id, userId).subscribe({
      next: () => this.advance(),
      complete: () => this.isSaving.set(false),
      error: () => this.isSaving.set(false),
    });
  }
}
